import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  NgZone,
  OnChanges,
  OnDestroy,
  AfterViewInit,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ChatSettingStyles } from '../../../services/chat.service';
import { buildPreviewPaint, ORBI_BUBBLE_SCRIPT, PreviewPaint } from '../chat-styling.component';
import { createLiquid } from './liquid';
import { svgMaskUrl } from '../icon-mask';

/** Abbruch eines laufenden Ablaufs (Reiter gewechselt, Seite verlassen). */
class Stopped extends Error {}

/**
 * Animierte Vorschau des Liquid-Interface auf der Interface-Seite (früher
 * Chat-Styling): ein kleines Liquid-Widget spielt in Dauerschleife auf und
 * zu, ein großer 3D-Mauszeiger führt durch — damit man den Flüssig-Effekt
 * sieht (Marcel, 27.10.2026).
 *
 *   scenario 'canvas': Zeiger klickt Orbi → Leiste fährt auf, Apps und
 *     Vorschläge steigen als Tropfen auf → Chat-App → Canvas wächst →
 *     Lernerprofil-App → Canvas wabbelt → Schließen → alles fließt zurück.
 *   scenario 'bar': Pop-up-Nachricht und Antworten ploppen über Orbi auf →
 *     Zeiger fährt über Orbi → Leiste fährt auf, Vorschläge kommen → Zeiger
 *     tippt etwas → zu.
 *   scenario 'aid' (Reiter Lernhilfen): Zeiger öffnet Orbi, tippt eine Frage
 *     passend zur Lernhilfe, sendet → Canvas mit der Frage, darunter die
 *     Lernhilfe als Antwort (der Inhalt dieser Komponente, `answer` startet
 *     ihr Laden). Danach Stillstand, die Lernhilfe ist klickbar; ein neuer
 *     `replayKey` (Blättern) tippt die nächste Frage.
 *
 * Nachbau von chat-widget › command-widget mit denselben Klassen und dem
 * Bewegungscode (liquid.ts, Kopie). Encapsulation None, weil liquid.ts eigenes
 * SVG baut — jede Regel hängt unter `.ld-stage`, Keyframes heißen `ld-*`.
 */
@Component({
  selector: 'app-liquid-demo',
  templateUrl: './liquid-demo.component.html',
  styleUrl: './liquid-demo.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class LiquidDemoComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() scenario: 'canvas' | 'bar' | 'aid' = 'canvas';
  /** Ohne Rahmen (Interface Chat): nur der Inhalt, keine Bühne. */
  @Input() on = true;
  @HostBinding('class.ld-host-off') get hostOff(): boolean {
    return !this.on;
  }
  /** Szenario 'aid': Wechsel (z. B. Blättern) spielt die nächste Frage ab. */
  @Input() replayKey: unknown;
  /** Szenario 'aid': die Antwort (Inhalt) erscheint jetzt im Chat. */
  @Output() answer = new EventEmitter<void>();
  /** Text, den der Zeiger in die Leiste tippt (übersetzt vom Aufrufer). */
  @Input() typeText = '';
  /** Nur im sichtbaren Reiter abspielen. */
  @Input() active = true;
  @Input() set styles(value: Partial<ChatSettingStyles> | null | undefined) {
    this._styles = value ?? {};
    this.paint = buildPreviewPaint(this._styles);
    this.refreshIconMask();
  }
  get styles(): Partial<ChatSettingStyles> {
    return this._styles;
  }
  private _styles: Partial<ChatSettingStyles> = {};

  @ViewChild('stage', { static: true }) stageRef!: ElementRef<HTMLElement>;
  @ViewChild('root', { static: true }) rootRef!: ElementRef<HTMLElement>;
  @ViewChild('canvas', { static: true }) canvasRef!: ElementRef<HTMLElement>;
  @ViewChild('apps', { static: true }) appsRef!: ElementRef<HTMLElement>;
  @ViewChild('suggest', { static: true }) suggestRef!: ElementRef<HTMLElement>;
  @ViewChild('orbi', { static: true }) orbiRef!: ElementRef<HTMLElement>;
  @ViewChild('input', { static: true }) inputRef!: ElementRef<HTMLElement>;
  @ViewChild('send', { static: true }) sendRef!: ElementRef<HTMLElement>;
  @ViewChild('chat', { static: true }) chatRef!: ElementRef<HTMLElement>;
  @ViewChild('cursor', { static: true }) cursorRef!: ElementRef<HTMLElement>;

  paint: PreviewPaint = buildPreviewPaint({});

  /** Icon-Größe im Dot/Blob wie im Widget, 16–44px. */
  get iconSize(): number {
    const size = Number(this.styles.chatBubbleIconSize) || 28;
    return Math.min(44, Math.max(16, size));
  }

  /** Icon-Farbe wie im Widget; ungesetzt weiß. */
  get iconColor(): string {
    const color = this.styles.chatBubbleIconColor;
    return typeof color === 'string' && /^#[0-9a-f]{3,8}$/i.test(color.trim()) ? color.trim() : '#ffffff';
  }

  /** Farbe nur der App-Symbole (`--oc-app-icon`) wie im Widget; ungesetzt
   *  null = wie die Schrift auf der Primärfarbe (weiß aus dem SCSS). */
  get appIconColor(): string | null {
    const color = this.styles.appIconColor;
    return typeof color === 'string' && /^#[0-9a-f]{3,8}$/i.test(color.trim()) ? color.trim() : null;
  }

  /** SVG-Icon als Maske (färbbar), sonst null — wie im Widget (icon-mask.ts). */
  iconMaskUrl: string | null = null;
  private iconMaskFor: string | null = null;

  private refreshIconMask(): void {
    const url = this._styles.chatBubbleIcon || null;
    if (url === this.iconMaskFor) return;
    this.iconMaskFor = url;
    this.iconMaskUrl = null;
    if (!url) return;
    svgMaskUrl(url).then((mask) => {
      if (this.iconMaskFor !== url) return;
      this.iconMaskUrl = mask;
      this.cdr.markForCheck();
      if (this.viewReady) this.cdr.detectChanges();
    });
  }

  /** Eigener Platzhalter des Stylings; leer oder der alte Standard → der
   *  Liquid-Standard (wie im Widget, text-input › placeholder). */
  get placeholderText(): string {
    const t = (this.styles.inputPlaceholder ?? '').trim();
    return !t || t === 'Schreibe eine Nachricht...' || t === 'Write a message...' ? '' : t;
  }

  /** Widget Trigger (chatBubbleStyle): 'glass' = Orbi, Standard. */
  get trigger(): 'orbi' | 'dot' | 'blob' {
    const style = this.styles.chatBubbleStyle;
    return style === 'dot' || style === 'blob' ? style : 'orbi';
  }
  readonly orbiScript = ORBI_BUBBLE_SCRIPT;

  /** Ansicht in der Canvas: Chat oder Lernerprofil. */
  view: 'chat' | 'profile' = 'chat';
  popup = false;
  typed = '';
  typing = false;
  orbiHover = false;
  /** Szenario 'aid': gestellte Frage (Nutzer-Blase) und ob die Antwort steht. */
  asked = '';
  answered = false;
  private aidDone = false;

  private liquid: ReturnType<typeof createLiquid> | null = null;
  private appsShown = false;
  private chipsShown = false;
  private canvasShown = false;
  private readonly chipIds = new WeakMap<Element, string>();
  private chipSeq = 0;
  private runId = 0;
  private viewReady = false;

  constructor(private zone: NgZone, private cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => {
      try {
        this.liquid = createLiquid(this.rootRef.nativeElement);
      } catch {
        this.liquid = null;
      }
    });
    this.viewReady = true;
    this.restart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.viewReady) return;
    if (changes['active'] || changes['scenario'] || changes['on']) {
      this.restart();
    } else if (changes['replayKey'] && this.scenario === 'aid') {
      // Blättern: steht die Canvas schon, nur die nächste Frage tippen.
      if (this.aidDone && this.on && this.active && !this.reducedMotion) {
        const id = ++this.runId;
        this.zone.runOutsideAngular(() => this.askAgain(id).catch(() => {}));
      } else {
        this.restart();
      }
    }
  }

  ngOnDestroy(): void {
    this.runId++;
    this.liquid?.clearAll();
  }

  get reducedMotion(): boolean {
    return typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // --- Ablauf -----------------------------------------------------------------

  private restart(): void {
    const id = ++this.runId;
    this.reset();
    if (!this.on || !this.active) return;
    if (this.reducedMotion) {
      // Ohne Bewegung: geöffneter Zustand als Standbild.
      this.setState(true, this.scenario !== 'bar', false);
      if (this.scenario === 'aid') {
        this.asked = this.typeText;
        this.showAnswer();
      }
      return;
    }
    if (this.scenario === 'aid') {
      this.zone.runOutsideAngular(() => this.playAid(id).catch(() => {}));
      return;
    }
    this.zone.runOutsideAngular(() => {
      this.loop(id).catch(() => {});
    });
  }

  private async loop(id: number): Promise<void> {
    try {
      while (id === this.runId) {
        if (this.scenario === 'canvas') await this.playCanvas(id);
        else await this.playBar(id);
      }
    } catch (e) {
      if (!(e instanceof Stopped)) throw e;
    }
  }

  private async playCanvas(id: number): Promise<void> {
    this.reset();
    await this.wait(id, 700);
    await this.moveTo(id, this.orbiRef.nativeElement);
    this.setHover(true);
    await this.click(id);
    this.setHover(false);
    this.setState(true, false);
    await this.wait(id, 1500);
    await this.moveTo(id, this.app('chat'));
    await this.click(id);
    this.setView('chat');
    this.press(this.app('chat'));
    this.setState(true, true);
    await this.wait(id, 2600);
    await this.moveTo(id, this.app('profile'));
    await this.click(id);
    this.setView('profile');
    this.liquid?.wobble(this.canvasRef.nativeElement);
    this.press(this.app('profile'));
    await this.wait(id, 2200);
    await this.moveTo(id, this.closeTool());
    await this.click(id);
    this.setState(false, false);
    await this.wait(id, 700);
    await this.park(id);
    await this.wait(id, 1200);
  }

  private async playBar(id: number): Promise<void> {
    this.reset();
    await this.wait(id, 900);
    this.setPopup(true);
    await this.wait(id, 2200);
    await this.moveTo(id, this.orbiRef.nativeElement);
    this.setHover(true);
    await this.wait(id, 250);
    this.setPopup(false);
    this.setState(true, false);
    this.setHover(false);
    await this.wait(id, 1500);
    await this.moveTo(id, this.inputRef.nativeElement, 0.85);
    await this.click(id);
    await this.type(id, this.typeText);
    await this.wait(id, 1400);
    await this.park(id);
    this.typed = '';
    this.detect();
    this.setState(false, false);
    await this.wait(id, 1600);
  }


  /** Szenario 'aid': Orbi öffnen, Frage tippen, senden, Lernhilfe erscheint. */
  private async playAid(id: number): Promise<void> {
    await this.wait(id, 600);
    await this.moveTo(id, this.orbiRef.nativeElement);
    this.setHover(true);
    await this.click(id);
    this.setHover(false);
    this.setState(true, false);
    await this.wait(id, 1300);
    await this.ask(id);
  }

  /** Blättern bei offener Canvas: nächste Frage tippen und senden. */
  private async askAgain(id: number): Promise<void> {
    this.answered = false;
    this.detect();
    const cursor = this.cursorRef.nativeElement;
    cursor.style.transition = 'opacity 0.25s ease';
    cursor.style.opacity = '1';
    await this.ask(id);
  }

  private async ask(id: number): Promise<void> {
    await this.moveTo(id, this.inputRef.nativeElement, 0.85);
    await this.click(id);
    await this.type(id, this.typeText);
    await this.wait(id, 350);
    await this.moveTo(id, this.sendRef.nativeElement);
    await this.click(id);
    this.asked = this.typed;
    this.typed = '';
    this.answered = false;
    this.setView('chat');
    if (!this.canvasShown) {
      this.press(this.app('chat'));
      this.setState(true, true);
      await this.wait(id, 650);
    } else {
      this.detect();
      await this.wait(id, 350);
    }
    this.showAnswer();
    this.aidDone = true;
    await this.wait(id, 500);
    await this.park(id);
    // Stillstand: Zeiger weg, die Lernhilfe gehört jetzt dem Nutzer.
    const cursor = this.cursorRef.nativeElement;
    cursor.style.transition = 'opacity 0.4s ease';
    cursor.style.opacity = '0';
  }

  /** Antwort zeigen, Laden der Lernhilfe starten (im Zone-Kontext, damit
   *  ihre Zeitgeber die Ansicht aktualisieren) und unten mitscrollen, solange
   *  sie lädt und wächst. */
  private showAnswer(): void {
    this.answered = true;
    this.detect();
    this.zone.run(() => this.answer.emit());
    const chat = this.chatRef.nativeElement;
    const until = Date.now() + 5000;
    const follow = () => {
      chat.scrollTop = chat.scrollHeight;
      if (Date.now() < until) requestAnimationFrame(follow);
    };
    requestAnimationFrame(follow);
  }

  // --- Zustände wie im Widget (command-widget › afterChange) ---------------------

  private reset(): void {
    this.liquid?.clearAll();
    const root = this.rootRef.nativeElement;
    root.dataset['state'] = 'closed';
    root.dataset['canvas'] = 'closed';
    root.classList.remove('is-sinking');
    this.appsShown = this.chipsShown = this.canvasShown = false;
    this.view = 'chat';
    this.popup = false;
    this.typed = '';
    this.typing = false;
    this.orbiHover = false;
    this.asked = '';
    this.answered = false;
    this.aidDone = false;
    this.pressReset();
    const cursor = this.cursorRef.nativeElement;
    cursor.style.transition = 'none';
    cursor.style.opacity = this.on && this.active && !this.reducedMotion ? '1' : '0';
    const start = this.parkPoint();
    cursor.style.transform = `translate(${start.x}px, ${start.y}px)`;
    this.detect();
  }

  /** Verzögerungen in ms bei 560 ms Dauer — liquid skaliert sie mit --oc-dur. */
  private setState(open: boolean, canvas: boolean, animate = true): void {
    const root = this.rootRef.nativeElement;
    const wantApps = open;
    const wantChips = open && !canvas;
    const wantCanvas = open && canvas;
    root.dataset['state'] = open ? 'open' : 'closed';
    root.dataset['canvas'] = wantCanvas ? 'open' : 'closed';
    const liquid = this.liquid;
    if (animate && liquid?.enabled) {
      const closing = !open && (this.appsShown || this.chipsShown || this.canvasShown);
      root.classList.toggle('is-sinking', closing);
      if (wantApps !== this.appsShown) {
        const list = Array.from(this.appsRef.nativeElement.querySelectorAll<HTMLElement>('.oc-app'));
        const items = list.map((el) => {
          const i = Number(el.style.getPropertyValue('--i')) || 0;
          return { el, group: 'apps', delay: wantApps ? 196 + i * 30 : (list.length - 1 - i) * 18 };
        });
        if (wantApps) liquid.rise(items);
        else liquid.sink(items).then(() => root.classList.remove('is-sinking'));
      }
      if (wantChips !== this.chipsShown) {
        const items = Array.from(this.suggestRef.nativeElement.querySelectorAll<HTMLElement>('.oc-chip')).map(
          (el, n) => ({ el, group: this.chipGroup(el), delay: wantChips ? 560 + n * 45 : n * 15 })
        );
        wantChips ? liquid.rise(items) : liquid.sink(items);
      }
      if (wantCanvas !== this.canvasShown) {
        wantCanvas
          ? liquid.grow(this.canvasRef.nativeElement, this.appsShown ? 0 : 300)
          : liquid.shrink(this.canvasRef.nativeElement);
      }
    }
    this.appsShown = wantApps;
    this.chipsShown = wantChips;
    this.canvasShown = wantCanvas;
  }

  private chipGroup(el: Element): string {
    if (!this.chipIds.has(el)) this.chipIds.set(el, `chip-${++this.chipSeq}`);
    return this.chipIds.get(el)!;
  }

  private setView(view: 'chat' | 'profile'): void {
    this.view = view;
    this.detect();
  }

  private setPopup(on: boolean): void {
    this.popup = on;
    this.detect();
  }

  private setHover(on: boolean): void {
    this.orbiHover = on;
    this.detect();
  }

  /** Gedrückte App wie im Widget (aria-pressed). */
  private press(el: HTMLElement): void {
    this.pressReset();
    el.setAttribute('aria-pressed', 'true');
  }

  private pressReset(): void {
    this.appsRef?.nativeElement
      .querySelectorAll('.oc-app[aria-pressed="true"]')
      .forEach((a) => a.setAttribute('aria-pressed', 'false'));
  }

  private app(id: string): HTMLElement {
    return this.appsRef.nativeElement.querySelector<HTMLElement>(`[data-app="${id}"]`)!;
  }

  private closeTool(): HTMLElement {
    return this.canvasRef.nativeElement.querySelector<HTMLElement>('.oc-tool--close')!;
  }

  private detect(): void {
    this.cdr.detectChanges();
  }

  // --- Mauszeiger ---------------------------------------------------------------

  /** Spitze des Zeigers im SVG (44px): links oben, leicht eingerückt. */
  private static readonly TIP = { x: 8, y: 5 };

  private parkPoint(): { x: number; y: number } {
    const stage = this.stageRef.nativeElement.getBoundingClientRect();
    return { x: stage.width * 0.18, y: stage.height * 0.42 };
  }

  private async moveTo(id: number, el: HTMLElement | null, fx = 0.5, fy = 0.5): Promise<void> {
    if (!el) return;
    const stage = this.stageRef.nativeElement.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    const x = r.left - stage.left + r.width * fx - LiquidDemoComponent.TIP.x;
    const y = r.top - stage.top + r.height * fy - LiquidDemoComponent.TIP.y;
    const cursor = this.cursorRef.nativeElement;
    cursor.style.transition = 'transform 950ms cubic-bezier(0.45, 0, 0.2, 1)';
    cursor.style.transform = `translate(${x}px, ${y}px)`;
    await this.wait(id, 980);
  }

  private async park(id: number): Promise<void> {
    const p = this.parkPoint();
    const cursor = this.cursorRef.nativeElement;
    cursor.style.transition = 'transform 900ms cubic-bezier(0.45, 0, 0.2, 1)';
    cursor.style.transform = `translate(${p.x}px, ${p.y}px)`;
    await this.wait(id, 900);
  }

  private async click(id: number): Promise<void> {
    const cursor = this.cursorRef.nativeElement;
    cursor.classList.remove('is-clicking');
    void cursor.offsetWidth;
    cursor.classList.add('is-clicking');
    await this.wait(id, 260);
  }

  private async type(id: number, text: string): Promise<void> {
    this.typing = true;
    for (const ch of text) {
      this.typed += ch;
      this.detect();
      await this.wait(id, 55 + Math.random() * 60);
    }
    this.typing = false;
    this.detect();
  }

  private wait(id: number, ms: number): Promise<void> {
    return new Promise((resolve, reject) =>
      setTimeout(() => (id === this.runId ? resolve() : reject(new Stopped())), ms)
    );
  }
}
